import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { AppPdfPage } from '../../ttg';
import ArticleCover from './ArticleCover';
import ArticleFooter from './ArticleFooter';
import Images from '../Images';

const DEFAULT_BRAND = '5 Dysfunctions of a Team';
const CONTENT_CLASS = 'ttg-article-pdf-content';
const CONTENT_WIDTH = '7.1in';
const CONTENT_MAX_HEIGHT = 860;

const buildMeta = (article) =>
	[
		article?.author && `By ${article.author}`,
		article?.readTime,
		article?.publishedDate && dayjs(article.publishedDate).format('MMMM D, YYYY'),
	]
		.filter(Boolean)
		.join('  ·  ');

const preloadImages = (content) => {
	const sources = Array.from(content.matchAll(/<img[^>]+src=["']([^"']+)["']/gi)).map((match) => match[1]);

	return Promise.all(
		sources.map(
			(src) =>
				new Promise((resolve) => {
					const image = new Image();
					image.onload = resolve;
					image.onerror = resolve;
					image.src = src;
				}),
		),
	);
};

const splitIntoPages = (content) => {
	const source = document.createElement('div');
	source.innerHTML = content;

	if (!source.children.length) {
		return [content];
	}

	const measurer = document.createElement('div');
	measurer.className = CONTENT_CLASS;
	measurer.style.cssText = `position:absolute;visibility:hidden;top:0;left:-9999px;width:${CONTENT_WIDTH};`;
	document.body.appendChild(measurer);

	const pages = [];
	let current = [];

	const flush = () => {
		if (current.length) {
			pages.push(current.join(''));
		}
		current = [];
		measurer.innerHTML = '';
	};

	const add = (node) => {
		measurer.appendChild(node.cloneNode(true));

		if (measurer.offsetHeight <= CONTENT_MAX_HEIGHT) {
			current.push(node.outerHTML);
			return;
		}

		measurer.removeChild(measurer.lastChild);

		if (current.length) {
			flush();
			add(node);
			return;
		}

		if (node.children.length) {
			Array.from(node.children).forEach(add);
			return;
		}

		current.push(node.outerHTML);
		flush();
	};

	Array.from(source.children).forEach(add);
	flush();

	document.body.removeChild(measurer);

	return pages;
};

const ArticleContent = ({ article }) => {
	const content = article?.content;
	const [pages, setPages] = useState([]);

	const paginate = useCallback(() => {
		setPages(splitIntoPages(content));
	}, [content]);

	useEffect(() => {
		if (!content) return;
		Promise.all([document.fonts.ready, preloadImages(content)]).then(paginate);
	}, [content, paginate]);

	const brand = article?.brand || DEFAULT_BRAND;

	return (
		<>
			<AppPdfPage sectionClassName={'ttg-article-pdf-page'}>
				<ArticleCover
					logo={article?.logo || Images.logo}
					title={article?.title}
					subtitle={article?.subtitle}
					meta={buildMeta(article)}
				/>
			</AppPdfPage>

			{pages.map((pageContent, index) => (
				<AppPdfPage key={index} sectionClassName={'ttg-article-pdf-page'}>
					<div className='ArticlePage'>
						<div
							className={`ArticlePage__content ${CONTENT_CLASS}`}
							dangerouslySetInnerHTML={{ __html: pageContent }}
						/>
						<ArticleFooter brand={brand} page={index + 2} />
					</div>
				</AppPdfPage>
			))}
		</>
	);
};

export default ArticleContent;
